import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import { getAllUsers, getUserTypes, updateUsers, deleteUser, getProfileMetaData, saveProfileMetaData } from '../api';
import ErrorModal from '../components/ErrorModal';
import Tooltip from '../components/Tooltip';
import ConfirmationModal from '../components/ConfirmationModal';
import ClubTermsModal from '../components/ClubTermsModal';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import 'react-datepicker/dist/react-datepicker.css';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import {
  buildMembershipMetaDataPayload,
  getMembershipFormFields,
  getMembershipUserTypeId,
  resolveMembershipApplicationUserTypeId,
  userTypeHasValidMembership
} from '../utils/membershipFormData';
import { interpolateMembershipForm, getMembershipFormVariables } from '../utils/membershipFormTemplate';
import {
  ADMIN_STATUS_FILTER_OPTIONS,
  buildRejectionBannerHtml,
  clubHasOnlineSignupUserTypes,
  getAdminRowStatusDisplay,
  getAdminRowStatusFilterKey,
  getAdminStatusSortKey,
  isExpiredApprovedMember,
  isMembershipExpired,
  resolveMemberStatusAfterApplicationSave,
  STATUS_BADGE_BASE_CLASS,
  STATUS_BADGE_CLASSES
} from '../utils/memberStatus';

const getMembershipPaymentTotal = (userType) => {
  const membership = userType?.membership || {};
  return (Number(membership.fee || 0) || 0) + (Number(membership.zapperFee || 0) || 0);
};

const ADMIN_PRIMARY_BUTTON_CLASS = 'rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap';
const ADMIN_APPROVE_BUTTON_CLASS = 'rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 whitespace-nowrap';
const ADMIN_REJECT_BUTTON_CLASS = 'rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 whitespace-nowrap';

const getWorkflowActionClassName = (variant) => {
  if (variant === 'approve') return ADMIN_APPROVE_BUTTON_CLASS;
  if (variant === 'reject') return ADMIN_REJECT_BUTTON_CLASS;
  return ADMIN_PRIMARY_BUTTON_CLASS;
};

const calculateActivationExpiry = (termMonths, baseDate = new Date()) => {
  const expiry = new Date(baseDate);
  const months = Number(termMonths);
  if (!Number.isFinite(months) || months <= 0) return null;
  expiry.setMonth(expiry.getMonth() + months);
  return expiry.toISOString();
};

const getActivationExpiryBaseDate = (targetUser) => {
  if (!targetUser?.expiresOn) return new Date();

  const existingExpiry = new Date(targetUser.expiresOn);
  if (Number.isNaN(existingExpiry.getTime())) return new Date();

  return isMembershipExpired(targetUser.expiresOn) ? existingExpiry : new Date();
};

const memberNeedsActivation = (targetUser, applicationUserTypeId) => {
  if (!applicationUserTypeId) return false;
  if (String(targetUser.userType) !== String(applicationUserTypeId)) return true;
  if (!targetUser.expiresOn) return true;
  return isMembershipExpired(targetUser.expiresOn);
};

const ClubAdmin = () => {
  const { user } = useContext(AuthContext);
  const { clubDetails } = useClub();
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [editedUsers, setEditedUsers] = useState({});
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('userType');
  const [compactSortOrder, setCompactSortOrder] = useState('asc');
  const [compactSortField, setCompactSortField] = useState('userType');
  const [selectedUserTypeFilters, setSelectedUserTypeFilters] = useState([]);
  const [isTypeFilterInitialized, setIsTypeFilterInitialized] = useState(false);
  const [showCompactTypeFilter, setShowCompactTypeFilter] = useState(false);
  const [compactTypeFilterPosition, setCompactTypeFilterPosition] = useState({ top: 0, left: 0 });
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  const [isStatusFilterInitialized, setIsStatusFilterInitialized] = useState(false);
  const [showCompactStatusFilter, setShowCompactStatusFilter] = useState(false);
  const [compactStatusFilterPosition, setCompactStatusFilterPosition] = useState({ top: 0, left: 0 });
  const [stringFilters, setStringFilters] = useState({ email: '', firstName: '', lastName: '' });
  const [dateFilterSelected, setDateFilterSelected] = useState({ createdOn: [], lastLogin: [], expiresOn: [] });
  const [isDateFiltersInitialized, setIsDateFiltersInitialized] = useState(false);
  const [activeFilterPopup, setActiveFilterPopup] = useState(null);
  const [filterPopupPosition, setFilterPopupPosition] = useState({ top: 0, left: 0 });
  const [dateTreeExpanded, setDateTreeExpanded] = useState({});
  const [compactExpiryEditor, setCompactExpiryEditor] = useState(null);
  const [compactTextEditor, setCompactTextEditor] = useState(null);
  const [compactUserTypeEditor, setCompactUserTypeEditor] = useState(null);
  const [compactCreditsEditor, setCompactCreditsEditor] = useState(null);
  const [userActionPopup, setUserActionPopup] = useState(null);
  const [metaDataEditor, setMetaDataEditor] = useState(null);
  const [stagedApplicationEdits, setStagedApplicationEdits] = useState({});
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [rejectApplicationPrompt, setRejectApplicationPrompt] = useState(null);
  const [rejectApplicationReason, setRejectApplicationReason] = useState('');
  const compactTypeFilterDropdownRef = useRef(null);
  const compactTypeFilterButtonRef = useRef(null);
  const compactStatusFilterDropdownRef = useRef(null);
  const compactStatusFilterButtonRef = useRef(null);
  const filterButtonRefs = useRef({});
  const filterPopupRef = useRef(null);

  const isSuperUserLoggedIn = user?.userTypeId === '1';
  const canViewAdminMetaData = user?.permissions?.clubAdmin?.canView;
  const canEditAdminMetaData = user?.permissions?.clubAdmin?.canEdit;

  const showStatusColumn = useMemo(
    () => clubHasOnlineSignupUserTypes(userTypes),
    [userTypes]
  );

  const userTypeById = useMemo(() => {
    const map = new Map();
    userTypes.forEach((userType) => map.set(String(userType.id), userType));
    return map;
  }, [userTypes]);

  const buildRefreshedProfileUser = (user, response, {
    hasClearedApplication,
    stagedMemberStatus,
    hasLocalExpiryEdit,
    hasLocalUserTypeEdit
  }) => {
    let refreshed = { ...user };

    if (!hasClearedApplication && !stagedMemberStatus && Object.prototype.hasOwnProperty.call(response, 'memberStatus')) {
      refreshed.memberStatus = response.memberStatus ?? null;
    }

    if (!hasLocalUserTypeEdit && Object.prototype.hasOwnProperty.call(response, 'userType')) {
      refreshed.userType = response.userType ? String(response.userType) : user.userType;
    }

    if (!hasLocalExpiryEdit && Object.prototype.hasOwnProperty.call(response, 'expiresOn')) {
      refreshed.expiresOn = response.expiresOn || null;
    }

    return refreshed;
  };

  const getEditorContextUser = () => {
    if (!metaDataEditor?.user) return null;
    const liveUser = users.find((entry) => entry.uuid === metaDataEditor.user.uuid);
    return liveUser ? { ...metaDataEditor.user, ...liveUser } : metaDataEditor.user;
  };

  const openApplicationEditor = async (targetUser) => {
    if (!canViewAdminMetaData) return;

    const staged = stagedApplicationEdits[targetUser.uuid];
    const hasClearedApplication = Boolean(editedUsers[targetUser.uuid]?.clearApplication);
    setMetaDataEditor({
      user: targetUser,
      fields: staged?.fields || {},
      userTypeId: hasClearedApplication
        ? String(targetUser.userType)
        : (staged?.userTypeId || String(targetUser.userType)),
      loading: true,
      saving: false,
      error: ''
    });

    try {
      const response = await getProfileMetaData(targetUser.uuid);
      const metaData = response?.metaData;
      const formUserTypeId = hasClearedApplication
        ? String(targetUser.userType)
        : (staged?.userTypeId || resolveMembershipApplicationUserTypeId(metaData, targetUser.userType));
      const fields = staged?.fields ?? getMembershipFormFields(metaData);
      const fetchedMemberStatus = hasClearedApplication
        ? null
        : (staged?.memberStatus ?? response?.memberStatus ?? null);
      const hasLocalExpiryEdit = Boolean(editedUsers[targetUser.uuid]?.expiresOn);
      const hasLocalUserTypeEdit = Boolean(editedUsers[targetUser.uuid]?.userType);
      const shouldSyncProfileFields = (!hasClearedApplication && !staged?.memberStatus && Object.prototype.hasOwnProperty.call(response, 'memberStatus'))
        || (!hasLocalExpiryEdit && Object.prototype.hasOwnProperty.call(response, 'expiresOn'))
        || (!hasLocalUserTypeEdit && Object.prototype.hasOwnProperty.call(response, 'userType'));
      const refreshedUser = buildRefreshedProfileUser(targetUser, response, {
        hasClearedApplication,
        stagedMemberStatus: staged?.memberStatus,
        hasLocalExpiryEdit,
        hasLocalUserTypeEdit
      });

      if (shouldSyncProfileFields) {
        setUsers((prev) => prev.map((u) => {
          if (u.uuid !== targetUser.uuid) return u;

          let updatedUser = { ...u };

          if (!hasClearedApplication && !staged?.memberStatus && Object.prototype.hasOwnProperty.call(response, 'memberStatus')) {
            updatedUser.memberStatus = response.memberStatus ?? null;
          }

          if (!hasLocalUserTypeEdit && Object.prototype.hasOwnProperty.call(response, 'userType')) {
            updatedUser.userType = response.userType ? String(response.userType) : u.userType;
          }

          if (!hasLocalExpiryEdit && Object.prototype.hasOwnProperty.call(response, 'expiresOn')) {
            updatedUser.expiresOn = response.expiresOn || null;
          }

          return updatedUser;
        }));

        ensureSyncedUserPassesFilters(refreshedUser);
      }

      const applicationUserType = userTypes.find((type) => String(type.id) === String(formUserTypeId));
      if (!applicationUserType?.htmlForm) {
        setMetaDataEditor(null);
        return;
      }

      setMetaDataEditor({
        user: refreshedUser,
        fields,
        userTypeId: formUserTypeId,
        memberStatus: fetchedMemberStatus,
        loading: false,
        saving: false,
        error: ''
      });
    } catch (err) {
      console.error('[ClubAdmin] Failed to load application data:', err);
      const formUserTypeId = hasClearedApplication
        ? String(targetUser.userType)
        : (staged?.userTypeId || String(targetUser.userType));

      setMetaDataEditor({
        user: targetUser,
        fields: staged?.fields || {},
        userTypeId: formUserTypeId,
        memberStatus: hasClearedApplication ? null : undefined,
        loading: false,
        saving: false,
        error: 'Unable to load application data.'
      });
    }
  };

  const getEffectiveMemberStatus = (uuid) => {
    if (metaDataEditor?.user?.uuid === uuid && Object.prototype.hasOwnProperty.call(metaDataEditor, 'memberStatus')) {
      return metaDataEditor.memberStatus;
    }

    const targetUser = users.find((entry) => entry.uuid === uuid);
    return targetUser?.memberStatus ?? null;
  };

  const stageApplicationChanges = ({
    targetUser,
    fields,
    userTypeId,
    memberStatus,
    valid = true,
    activateMembership = false
  }) => {
    const resolvedMemberStatus = resolveMemberStatusAfterApplicationSave(memberStatus, { valid });
    const metaDataPayload = buildMembershipMetaDataPayload(fields, { userTypeId });
    const shouldActivate = activateMembership
      && resolvedMemberStatus.application === 'approved'
      && resolvedMemberStatus.paid === true
      && memberNeedsActivation(targetUser, userTypeId);
    const activationUserType = userTypes.find((type) => String(type.id) === String(userTypeId));
    const activationExpiry = shouldActivate
      ? calculateActivationExpiry(
        activationUserType?.membership?.term,
        getActivationExpiryBaseDate(targetUser)
      )
      : null;

    setStagedApplicationEdits((prev) => ({
      ...prev,
      [targetUser.uuid]: {
        fields,
        userTypeId,
        memberStatus: resolvedMemberStatus,
        metaDataPayload
      }
    }));

    setUsers((prev) => prev.map((u) => {
      if (u.uuid !== targetUser.uuid) return u;

      let updatedUser = { ...u, memberStatus: resolvedMemberStatus };
      if (shouldActivate && activationExpiry) {
        updatedUser = {
          ...updatedUser,
          userType: String(userTypeId),
          expiresOn: activationExpiry
        };
      }
      return updatedUser;
    }));

    setEditedUsers((prev) => {
      const existing = prev[targetUser.uuid] && typeof prev[targetUser.uuid] === 'object'
        ? prev[targetUser.uuid]
        : {};
      const flags = {
        ...existing,
        memberStatus: true,
        applicationData: true
      };

      if (shouldActivate && activationExpiry) {
        flags.userType = true;
        flags.expiresOn = true;
      }

      return {
        ...prev,
        [targetUser.uuid]: flags
      };
    });

    setMetaDataEditor(null);
  };

  const handleStageApplicationSave = (formData, validationResult = { valid: true }) => {
    if (!metaDataEditor?.user || !canEditAdminMetaData) return;

    const currentStatus = getEffectiveMemberStatus(metaDataEditor.user.uuid) || {
      paid: false,
      application: 'pending'
    };

    stageApplicationChanges({
      targetUser: metaDataEditor.user,
      fields: formData ?? metaDataEditor.fields,
      userTypeId: metaDataEditor.userTypeId,
      memberStatus: currentStatus,
      valid: validationResult.valid !== false
    });
  };

  const handleApproveApplication = (formData, validationResult = { valid: true }) => {
    if (!metaDataEditor?.user || !canEditAdminMetaData) return;

    const applicationUserType = userTypes.find(
      (type) => String(type.id) === String(metaDataEditor.userTypeId)
    );
    const totalPayment = getMembershipPaymentTotal(applicationUserType);

    stageApplicationChanges({
      targetUser: metaDataEditor.user,
      fields: formData ?? metaDataEditor.fields,
      userTypeId: metaDataEditor.userTypeId,
      memberStatus: {
        paid: totalPayment <= 0,
        application: 'approved'
      },
      valid: validationResult.valid !== false,
      activateMembership: totalPayment <= 0
    });
  };

  const handleRejectApplicationClick = (formData) => {
    if (!metaDataEditor?.user || !canEditAdminMetaData) return;
    setRejectApplicationReason('');
    setRejectApplicationPrompt({
      user: metaDataEditor.user,
      formData: formData ?? metaDataEditor.fields
    });
  };

  const handleRejectApplicationConfirm = () => {
    if (!metaDataEditor?.user || !canEditAdminMetaData || !rejectApplicationPrompt) return;

    const reason = rejectApplicationReason.trim();
    if (!reason) return;

    stageApplicationChanges({
      targetUser: metaDataEditor.user,
      fields: rejectApplicationPrompt.formData ?? metaDataEditor.fields,
      userTypeId: metaDataEditor.userTypeId,
      memberStatus: {
        paid: false,
        application: 'rejected',
        rejectionReason: reason
      },
      valid: true
    });

    setRejectApplicationPrompt(null);
    setRejectApplicationReason('');
  };

  const handlePaymentReceived = (formData, validationResult = { valid: true }) => {
    if (!metaDataEditor?.user || !canEditAdminMetaData) return;

    stageApplicationChanges({
      targetUser: metaDataEditor.user,
      fields: formData ?? metaDataEditor.fields,
      userTypeId: metaDataEditor.userTypeId,
      memberStatus: {
        paid: true,
        application: 'approved'
      },
      valid: validationResult.valid !== false,
      activateMembership: true
    });
  };

  const getApplicationWorkflowLeftActions = () => {
    if (!metaDataEditor?.user || !canEditAdminMetaData) return [];

    const editorUser = getEditorContextUser();
    const memberStatus = getEffectiveMemberStatus(editorUser.uuid);
    if (!memberStatus) return [];

    if (memberStatus.application === 'pending') {
      return [
        {
          label: 'Approve',
          variant: 'approve',
          requiresFormData: true,
          onClick: handleApproveApplication
        },
        {
          label: 'Reject',
          variant: 'reject',
          requiresFormData: true,
          onClick: handleRejectApplicationClick
        }
      ];
    }

    if (memberStatus.application === 'rejected') {
      return [{
        label: 'Approve',
        variant: 'approve',
        requiresFormData: true,
        onClick: handleApproveApplication
      }];
    }

    if (memberStatus.application === 'approved' && memberStatus.paid !== true) {
      return [{
        label: 'Payment Received',
        variant: 'primary',
        requiresFormData: true,
        onClick: handlePaymentReceived
      }];
    }

    if (isExpiredApprovedMember(memberStatus, editorUser.expiresOn)) {
      return [{
        label: 'Payment Received',
        variant: 'primary',
        requiresFormData: true,
        onClick: handlePaymentReceived
      }];
    }

    return [];
  };

  const metaDataApplicationUserType = useMemo(() => {
    if (!metaDataEditor?.userTypeId) return null;
    return userTypes.find((type) => String(type.id) === String(metaDataEditor.userTypeId)) || null;
  }, [metaDataEditor?.userTypeId, userTypes]);

  const metaDataApplicationHtml = useMemo(() => {
    if (!metaDataEditor?.user || !metaDataApplicationUserType?.htmlForm) return '';

    return interpolateMembershipForm(
      metaDataApplicationUserType.htmlForm,
      getMembershipFormVariables(metaDataEditor.user, {
        userTypeName: metaDataApplicationUserType.name,
        clubName: clubDetails?.name,
      })
    );
  }, [metaDataEditor?.user, metaDataApplicationUserType, clubDetails?.name]);

  const applicationRejectionPrependHtml = useMemo(() => {
    if (!metaDataEditor?.user) return '';

    const memberStatus = getEffectiveMemberStatus(metaDataEditor.user.uuid);
    if (memberStatus?.application !== 'rejected' || !memberStatus?.rejectionReason) return '';

    return buildRejectionBannerHtml(memberStatus.rejectionReason, { audience: 'admin' });
  }, [metaDataEditor?.user, metaDataEditor?.memberStatus, users]);

  const labelClass = "block text-sm font-medium text-slate-600 md:text-base";
  const textInputInTableCellClass = "w-full text-sm px-2 py-1.5 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 md:text-base md:px-3 md:py-2 h-[34px] md:h-[38px]";
  const selectClass = textInputInTableCellClass;
  const thClass = "px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap md:px-3 md:py-3 md:text-sm";
  const tdClass = "px-2 py-2 align-middle whitespace-nowrap text-sm text-slate-600 md:px-3 md:py-3 md:text-base";
  const buttonClass = "py-2 px-4 rounded-md text-white font-medium transition-all duration-200 flex items-center justify-center bg-blue-600 hover:bg-blue-700 active:bg-blue-800";

  // Masked input for date fields to force numeric entry and auto-insert dashes (dd-mm-yyyy)
  const MaskedDateInput = React.forwardRef((props, ref) => (
    <InputMask
      mask="99-99-9999"
      maskChar={null}
      inputMode="numeric"
      placeholder="dd-mm-yyyy"
      inputRef={ref}
      {...props}
    />
  ));

  useEffect(() => {
    if (!user?.permissions?.clubAdmin?.canView) {
      setError('You do not have permissions to view this page');
      setIsErrorModalOpen(true);
      setIsDataLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setIsDataLoading(true);
        const [usersData, userTypesData] = await Promise.all([
          getAllUsers(),
          getUserTypes()
        ]);
        setUsers(usersData);
        setUserTypes([...userTypesData].sort((a, b) => Number(a.id) - Number(b.id)));
      } catch (err) {
        console.error(err);
        handleError('Failed to fetch users. Please try again.');
      } finally {
        setIsDataLoading(false);
      }
    };
    fetchData();
  }, [user]);

  useEffect(() => {
    if (isTypeFilterInitialized || userTypes.length === 0) return;
    const defaultTypeIds = userTypes.map((ut) => String(ut.id));
    setSelectedUserTypeFilters(defaultTypeIds);
    setIsTypeFilterInitialized(true);
  }, [userTypes, isTypeFilterInitialized]);

  useEffect(() => {
    if (isStatusFilterInitialized || !showStatusColumn) return;
    setSelectedStatusFilters(ADMIN_STATUS_FILTER_OPTIONS.map((option) => option.key));
    setIsStatusFilterInitialized(true);
  }, [showStatusColumn, isStatusFilterInitialized]);

  useEffect(() => {
    if (isDateFiltersInitialized || users.length === 0) return;
    setDateFilterSelected({
      createdOn: getAllDateKeysForField('createdOn'),
      lastLogin: getAllDateKeysForField('lastLogin'),
      expiresOn: getAllDateKeysForField('expiresOn'),
    });
    setIsDateFiltersInitialized(true);
  }, [users, isDateFiltersInitialized]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideTypeButton = compactTypeFilterButtonRef.current?.contains(event.target);
      const clickedInsideTypeDropdown = compactTypeFilterDropdownRef.current?.contains(event.target);
      if (!clickedInsideTypeButton && !clickedInsideTypeDropdown) {
        setShowCompactTypeFilter(false);
      }
      const clickedInsideStatusButton = compactStatusFilterButtonRef.current?.contains(event.target);
      const clickedInsideStatusDropdown = compactStatusFilterDropdownRef.current?.contains(event.target);
      if (!clickedInsideStatusButton && !clickedInsideStatusDropdown) {
        setShowCompactStatusFilter(false);
      }
      const clickedInsideAnyFilterButton = Object.values(filterButtonRefs.current).some(
        (el) => el?.contains(event.target)
      );
      if (!clickedInsideAnyFilterButton && !filterPopupRef.current?.contains(event.target)) {
        setActiveFilterPopup(null);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showCompactTypeFilter) return;

    const updateFilterPosition = () => {
      const triggerRect = compactTypeFilterButtonRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      const dropdownWidth = 256; // matches w-64
      const maxLeft = window.innerWidth - dropdownWidth - 8;
      const nextLeft = Math.max(8, Math.min(triggerRect.left, maxLeft));
      setCompactTypeFilterPosition({
        top: triggerRect.bottom + 4,
        left: nextLeft
      });
    };

    updateFilterPosition();
    window.addEventListener('resize', updateFilterPosition);
    window.addEventListener('scroll', updateFilterPosition, true);
    return () => {
      window.removeEventListener('resize', updateFilterPosition);
      window.removeEventListener('scroll', updateFilterPosition, true);
    };
  }, [showCompactTypeFilter]);

  useEffect(() => {
    if (!showCompactStatusFilter) return;

    const updateFilterPosition = () => {
      const triggerRect = compactStatusFilterButtonRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      const dropdownWidth = 256;
      const maxLeft = window.innerWidth - dropdownWidth - 8;
      const nextLeft = Math.max(8, Math.min(triggerRect.left, maxLeft));
      setCompactStatusFilterPosition({
        top: triggerRect.bottom + 4,
        left: nextLeft
      });
    };

    updateFilterPosition();
    window.addEventListener('resize', updateFilterPosition);
    window.addEventListener('scroll', updateFilterPosition, true);
    return () => {
      window.removeEventListener('resize', updateFilterPosition);
      window.removeEventListener('scroll', updateFilterPosition, true);
    };
  }, [showCompactStatusFilter]);

  useEffect(() => {
    if (!activeFilterPopup) return;
    const updatePosition = () => {
      const triggerRect = filterButtonRefs.current[activeFilterPopup]?.getBoundingClientRect();
      if (!triggerRect) return;
      const popupWidth = ['email', 'firstName', 'lastName'].includes(activeFilterPopup) ? 280 : 256;
      const maxLeft = window.innerWidth - popupWidth - 8;
      setFilterPopupPosition({ top: triggerRect.bottom + 4, left: Math.max(8, Math.min(triggerRect.left, maxLeft)) });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [activeFilterPopup]);


  // Add this function to convert to South Africa time
  const getSouthAfricaTime = () => {
    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000)); // Adjust to UTC
    return new Date(utcTime.getTime() + (2 * 60 * 60 * 1000)); // Adjust to South Africa time (UTC+2)
  };

  const handleEditUser = (uuid, field, value) => {
    if (field === 'expiresOn' && value) {
      value = value.toISOString();
    }

    const previousUser = users.find((u) => u.uuid === uuid);
    const isUserTypeChange = field === 'userType'
      && previousUser
      && String(previousUser.userType) !== String(value);

    setUsers(prev => {
      const updatedUsers = prev.map(u => {
        if (u.uuid === uuid) {
          const updatedUser = { ...u, [field]: value };

          if (isUserTypeChange) {
            updatedUser.memberStatus = null;

            const newUserType = userTypes.find(ut => String(ut.id) === String(value));
            if (!userTypeHasValidMembership(newUserType)) {
              updatedUser.expiresOn = null;
            }
          }

          return updatedUser;
        }
        return u;
      });

      return updatedUsers;
    });

    if (isUserTypeChange) {
      setStagedApplicationEdits((prev) => {
        const next = { ...prev };
        delete next[uuid];
        return next;
      });
      setMetaDataEditor((prev) => (prev?.user?.uuid === uuid ? null : prev));
    }

    setEditedUsers(prev => ({
      ...prev,
      [uuid]: {
        ...(prev[uuid] && typeof prev[uuid] === 'object' ? prev[uuid] : {}),
        [field]: true,
        ...(isUserTypeChange ? { memberStatus: true, clearApplication: true } : {})
      }
    }));
  };

  const handleUpdateAll = async () => {
    const superUsers = users.filter(u => String(u.userType) === '1');
    if (superUsers.length === 0) {
      handleError('There must be at least one Super User.');
      return;
    }

    const usersToUpdate = users.filter(u => editedUsers[u.uuid]);
    if (usersToUpdate.length === 0) return;

    setLoadingUpdate(true);
    try {
      for (const u of usersToUpdate) {
        const staged = stagedApplicationEdits[u.uuid];
        const shouldClearApplication = editedUsers[u.uuid]?.clearApplication;

        if (shouldClearApplication) {
          const profileMeta = await getProfileMetaData(u.uuid);
          const fields = getMembershipFormFields(profileMeta?.metaData);
          await saveProfileMetaData(
            buildMembershipMetaDataPayload(fields, { userTypeId: u.userType }),
            u.uuid,
            { memberStatus: null }
          );
        }

        if (staged?.metaDataPayload) {
          await saveProfileMetaData(staged.metaDataPayload, u.uuid, {
            memberStatus: staged.memberStatus
          });
        }
      }

      await updateUsers(usersToUpdate.map((u) => ({
        uuid: u.uuid,
        firstName: u.firstName,
        lastName: u.lastName,
        userType: u.userType,
        expiresOn: u.expiresOn,
        credits: Number(u.credits ?? 0),
        ...(editedUsers[u.uuid]?.memberStatus ? { memberStatus: u.memberStatus ?? null } : {})
      })));
      const freshUsers = await getAllUsers();
      setUsers(freshUsers);

      // After a successful save, extend the active filters to include any new
      // values introduced by the edited rows so those rows stay visible.
      const updatedUuids = new Set(usersToUpdate.map((u) => u.uuid));
      const savedUsers = freshUsers.filter((u) => updatedUuids.has(u.uuid));

      setDateFilterSelected((prev) => {
        const next = { ...prev };
        for (const field of ['expiresOn']) {
          const newKeys = savedUsers
            .map((u) => getDateKey(u[field]))
            .filter((k) => k && !prev[field].includes(k));
          if (newKeys.length) next[field] = [...prev[field], ...newKeys];
        }
        return next;
      });

      setSelectedUserTypeFilters((prev) => {
        const newTypes = savedUsers
          .map((u) => String(u.userType))
          .filter((t) => !prev.includes(t));
        return newTypes.length ? [...prev, ...newTypes] : prev;
      });

      setEditedUsers({});
      setStagedApplicationEdits({});
    } catch (err) {
      console.error(err);
      handleError('Failed to update users. Please try again.');
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleError = (message) => {
    setError(message);
    setIsErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setError('');
    setIsErrorModalOpen(false);
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const toggleCompactSort = (field) => {
    if (compactSortField === field) {
      setCompactSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setCompactSortField(field);
      setCompactSortOrder('asc');
    }
  };

  const getDateKey = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getAllDateKeysForField = (field) =>
    [...new Set(users.map((u) => getDateKey(u[field])).filter(Boolean))];

  const buildDateTree = (field) => {
    const tree = {};
    users.forEach((u) => {
      const key = getDateKey(u[field]);
      if (!key) return;
      const [year, month, day] = key.split('-');
      if (!tree[year]) tree[year] = {};
      if (!tree[year][month]) tree[year][month] = new Set();
      tree[year][month].add(day);
    });
    const result = {};
    Object.keys(tree).sort().reverse().forEach((year) => {
      result[year] = {};
      Object.keys(tree[year]).sort().forEach((month) => {
        result[year][month] = [...tree[year][month]].sort((a, b) => Number(a) - Number(b));
      });
    });
    return result;
  };

  const formatDateDDMMYYYY = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = `${date.getFullYear()}`.slice(-2);
    return `${day} ${month} ${year}`;
  };

  const formatDateForInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseInputDate = (value) => {
    if (!value || !value.trim()) return null;
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) return null;

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);

    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return parsed;
  };

  const getDateForSorting = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return 'none';
    const expiry = new Date(expiryDate);
    if (Number.isNaN(expiry.getTime())) return 'none';
    const now = getSouthAfricaTime();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 7) return 'expiring';
    return 'active';
  };

  const ensureSyncedUserPassesFilters = (syncedUser) => {
    if (!syncedUser) return;

    if (isTypeFilterInitialized) {
      const userTypeId = String(syncedUser.userType || '');
      if (userTypeId) {
        setSelectedUserTypeFilters((prev) => (
          prev.includes(userTypeId) ? prev : [...prev, userTypeId]
        ));
      }
    }

    if (showStatusColumn && isStatusFilterInitialized) {
      const statusKey = getAdminRowStatusFilterKey(
        syncedUser.memberStatus,
        userTypeById.get(String(syncedUser.userType)),
        { isExpired: getExpiryStatus(syncedUser.expiresOn) === 'expired' }
      );
      if (statusKey) {
        setSelectedStatusFilters((prev) => (
          prev.includes(statusKey) ? prev : [...prev, statusKey]
        ));
      }
    }

    if (isDateFiltersInitialized) {
      const expiryKey = getDateKey(syncedUser.expiresOn);
      if (expiryKey) {
        setDateFilterSelected((prev) => {
          if (prev.expiresOn.includes(expiryKey)) return prev;
          return { ...prev, expiresOn: [...prev.expiresOn, expiryKey] };
        });
      }
    }
  };

  const getUserTypeName = (userTypeId) => {
    return userTypes.find((ut) => String(ut.id) === String(userTypeId))?.name || String(userTypeId || '');
  };

  const renderSortButton = (field) => (
    <button onClick={() => toggleSort(field)} className="ml-1 inline-flex items-center">
      {sortField === field ? (
        sortOrder === 'asc' ? (
          <ArrowUpIcon className="h-4 w-4 text-slate-600" />
        ) : (
          <ArrowDownIcon className="h-4 w-4 text-slate-600" />
        )
      ) : (
        <ArrowUpIcon className="h-4 w-4 text-slate-600" />
      )}
    </button>
  );

  const renderCompactSortButton = (field, className = "ml-1 inline-flex items-center") => (
    <button onClick={() => toggleCompactSort(field)} className={className}>
      {compactSortField === field ? (
        compactSortOrder === 'asc' ? (
          <ArrowUpIcon className="h-4 w-4 text-slate-600" />
        ) : (
          <ArrowDownIcon className="h-4 w-4 text-slate-600" />
        )
      ) : (
        <ArrowUpIcon className="h-4 w-4 text-slate-600" />
      )}
    </button>
  );

  const sortUsersByField = (usersToSort) => [...usersToSort].sort((a, b) => {
    if (sortField === 'userType') {
      const aType = Number(a.userType);
      const bType = Number(b.userType);
      return sortOrder === 'asc' ? aType - bType : bType - aType;
    }

    if (sortField === 'firstName' || sortField === 'lastName' || sortField === 'email') {
      const aValue = (a[sortField] || '').toLowerCase();
      const bValue = (b[sortField] || '').toLowerCase();
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (sortField === 'createdOn' || sortField === 'expiresOn') {
      const aDate = getDateForSorting(a[sortField]);
      const bDate = getDateForSorting(b[sortField]);

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;

      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    }

    if (sortField === 'credits') {
      const aCredits = Number(a.credits ?? 0);
      const bCredits = Number(b.credits ?? 0);
      return sortOrder === 'asc' ? aCredits - bCredits : bCredits - aCredits;
    }

    return 0;
  });

  const compactUserTypeOptions = userTypes;

  const toggleUserTypeFilter = (typeId) => {
    const normalizedTypeId = String(typeId);
    setSelectedUserTypeFilters((prev) => (
      prev.includes(normalizedTypeId)
        ? prev.filter((id) => id !== normalizedTypeId)
        : [...prev, normalizedTypeId]
    ));
  };

  const toggleStatusFilter = (statusKey) => {
    setSelectedStatusFilters((prev) => (
      prev.includes(statusKey)
        ? prev.filter((key) => key !== statusKey)
        : [...prev, statusKey]
    ));
  };

  const handleCompactTypeFilterToggle = () => {
    if (!showCompactTypeFilter) {
      const triggerRect = compactTypeFilterButtonRef.current?.getBoundingClientRect();
      if (triggerRect) {
        const dropdownWidth = 256;
        const maxLeft = window.innerWidth - dropdownWidth - 8;
        const nextLeft = Math.max(8, Math.min(triggerRect.left, maxLeft));
        setCompactTypeFilterPosition({
          top: triggerRect.bottom + 4,
          left: nextLeft
        });
      }
    }
    setShowCompactTypeFilter((prev) => !prev);
  };

  const handleCompactStatusFilterToggle = () => {
    if (!showCompactStatusFilter) {
      const triggerRect = compactStatusFilterButtonRef.current?.getBoundingClientRect();
      if (triggerRect) {
        const dropdownWidth = 256;
        const maxLeft = window.innerWidth - dropdownWidth - 8;
        const nextLeft = Math.max(8, Math.min(triggerRect.left, maxLeft));
        setCompactStatusFilterPosition({
          top: triggerRect.bottom + 4,
          left: nextLeft
        });
      }
    }
    setShowCompactStatusFilter((prev) => !prev);
  };

  const openFilterPopup = (field) => {
    if (activeFilterPopup === field) {
      setActiveFilterPopup(null);
      return;
    }
    const triggerRect = filterButtonRefs.current[field]?.getBoundingClientRect();
    if (triggerRect) {
      const popupWidth = ['email', 'firstName', 'lastName'].includes(field) ? 280 : 256;
      const maxLeft = window.innerWidth - popupWidth - 8;
      setFilterPopupPosition({ top: triggerRect.bottom + 4, left: Math.max(8, Math.min(triggerRect.left, maxLeft)) });
    }
    setActiveFilterPopup(field);
  };

  const hasMembershipForUserType = (userTypeId) => {
    const matchingType = userTypes.find((ut) => String(ut.id) === String(userTypeId));
    return userTypeHasValidMembership(matchingType);
  };

  const openCompactExpiryEditor = (userItem) => {
    if (!hasMembershipForUserType(userItem.userType)) return;
    setCompactExpiryEditor({
      uuid: userItem.uuid,
      value: formatDateForInput(userItem.expiresOn),
      firstName: userItem.firstName,
      lastName: userItem.lastName,
    });
  };

  const handleCompactExpiryChange = (date) => {
    if (!compactExpiryEditor) return;
    setCompactExpiryEditor((prev) => ({
      ...prev,
      value: formatDateForInput(date)
    }));
  };

  const saveCompactExpiryEditor = () => {
    if (!compactExpiryEditor?.uuid) return;

    if (!compactExpiryEditor.value || !compactExpiryEditor.value.trim()) {
      handleEditUser(compactExpiryEditor.uuid, 'expiresOn', null);
      setCompactExpiryEditor(null);
      return;
    }

    const parsedDate = parseInputDate(compactExpiryEditor.value);
    if (!parsedDate) {
      handleError('Please enter a valid date in dd-mm-yyyy format.');
      return;
    }

    handleEditUser(compactExpiryEditor.uuid, 'expiresOn', parsedDate);
    setCompactExpiryEditor(null);
  };

  const openUserActionPopup = (userItem) => {
    setUserActionPopup({ user: userItem });
  };

  const openCompactTextEditor = (userItem, field) => {
    setCompactTextEditor({
      uuid: userItem.uuid,
      field,
      value: userItem[field] || '',
    });
  };

  const saveCompactTextEditor = () => {
    if (!compactTextEditor?.uuid || !compactTextEditor?.field) return;
    handleEditUser(compactTextEditor.uuid, compactTextEditor.field, compactTextEditor.value);
    setCompactTextEditor(null);
  };

  const openCompactUserTypeEditor = (userItem) => {
    setCompactUserTypeEditor({
      uuid: userItem.uuid,
      value: String(userItem.userType),
      firstName: userItem.firstName,
      lastName: userItem.lastName,
    });
  };

  const saveCompactUserTypeEditor = () => {
    if (!compactUserTypeEditor?.uuid) return;
    handleEditUser(compactUserTypeEditor.uuid, 'userType', compactUserTypeEditor.value);
    setCompactUserTypeEditor(null);
  };

  const openCompactCreditsEditor = (userItem) => {
    setCompactCreditsEditor({
      uuid: userItem.uuid,
      currentCredits: Number(userItem.credits ?? 0),
      addAmount: '',
      firstName: userItem.firstName,
      lastName: userItem.lastName,
    });
  };

  const saveCompactCreditsEditor = () => {
    if (!compactCreditsEditor?.uuid) return;
    const addAmount = Number(compactCreditsEditor.addAmount);
    if (!Number.isFinite(addAmount) || addAmount <= 0) {
      handleError('Please enter a valid positive credit amount to add.');
      return;
    }
    const updatedCredits = Number((compactCreditsEditor.currentCredits + addAmount).toFixed(2));
    handleEditUser(compactCreditsEditor.uuid, 'credits', updatedCredits);
    setCompactCreditsEditor(null);
  };

  const compactUsersRaw = users;

  const dateFilterSets = useMemo(() => ({
    createdOn: new Set(dateFilterSelected.createdOn),
    lastLogin: new Set(dateFilterSelected.lastLogin),
    expiresOn: new Set(dateFilterSelected.expiresOn),
  }), [dateFilterSelected]);

  const isDateFilterActive = (field) =>
    isDateFiltersInitialized && getAllDateKeysForField(field).some((k) => !dateFilterSets[field].has(k));

  const isUserTypeFilterActive = isTypeFilterInitialized
    && userTypes.some((ut) => !selectedUserTypeFilters.includes(String(ut.id)));

  const isStatusFilterActive = isStatusFilterInitialized
    && ADMIN_STATUS_FILTER_OPTIONS.some((option) => !selectedStatusFilters.includes(option.key));

  const compactUsersFiltered = compactUsersRaw.filter((u) => {
    // Always keep rows with unsaved edits visible so the user can save them.
    if (editedUsers[u.uuid]) return true;
    if (selectedUserTypeFilters.length === 0 || !selectedUserTypeFilters.includes(String(u.userType))) return false;
    if (showStatusColumn && isStatusFilterInitialized) {
      const statusKey = getAdminRowStatusFilterKey(
        u.memberStatus,
        userTypeById.get(String(u.userType)),
        { isExpired: getExpiryStatus(u.expiresOn) === 'expired' }
      );
      if (selectedStatusFilters.length === 0 || !selectedStatusFilters.includes(statusKey)) return false;
    }
    for (const field of ['email', 'firstName', 'lastName']) {
      const val = stringFilters[field].trim().toLowerCase();
      if (val && !(u[field] || '').toLowerCase().includes(val)) return false;
    }
    if (isDateFiltersInitialized) {
      for (const field of ['createdOn', 'lastLogin', 'expiresOn']) {
        const key = getDateKey(u[field]);
        if (key && !dateFilterSets[field].has(key)) return false;
      }
    }
    return true;
  });

  const compactUsersSorted = [...compactUsersFiltered].sort((a, b) => {
    if (compactSortField === 'email' || compactSortField === 'firstName' || compactSortField === 'lastName') {
      const aValue = (a[compactSortField] || '').toLowerCase();
      const bValue = (b[compactSortField] || '').toLowerCase();
      return compactSortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (compactSortField === 'createdOn' || compactSortField === 'lastLogin' || compactSortField === 'expiresOn') {
      const aDate = getDateForSorting(a[compactSortField]);
      const bDate = getDateForSorting(b[compactSortField]);

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;

      return compactSortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    }

    if (compactSortField === 'userType') {
      const aTypeId = Number(a.userType);
      const bTypeId = Number(b.userType);
      if (Number.isFinite(aTypeId) && Number.isFinite(bTypeId)) {
        return compactSortOrder === 'asc' ? aTypeId - bTypeId : bTypeId - aTypeId;
      }
      const aType = String(a.userType ?? '');
      const bType = String(b.userType ?? '');
      return compactSortOrder === 'asc'
        ? aType.localeCompare(bType)
        : bType.localeCompare(aType);
    }

    if (compactSortField === 'credits') {
      const aCredits = Number(a.credits ?? 0);
      const bCredits = Number(b.credits ?? 0);
      return compactSortOrder === 'asc' ? aCredits - bCredits : bCredits - aCredits;
    }

    if (compactSortField === 'memberStatus') {
      const aType = userTypeById.get(String(a.userType));
      const bType = userTypeById.get(String(b.userType));
      const aKey = getAdminStatusSortKey(
        a.memberStatus,
        aType,
        { isExpired: getExpiryStatus(a.expiresOn) === 'expired' }
      );
      const bKey = getAdminStatusSortKey(
        b.memberStatus,
        bType,
        { isExpired: getExpiryStatus(b.expiresOn) === 'expired' }
      );
      return compactSortOrder === 'asc' ? aKey - bKey : bKey - aKey;
    }

    return 0;
  });

  const renderTable = (filteredUsers, showSort = true) => (
    <section className="mb-8 p-3 md:p-6 bg-white rounded-lg shadow border-2 border-slate-300">
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto divide-y divide-slate-200">
          <thead>
            <tr className="bg-slate-50">
              <th className={thClass}>
                Email
                {showSort && renderSortButton('email')}
              </th>
              <th className={`${thClass} w-[96px] min-w-[96px]`}>
                <span className="inline-flex items-center">
                  Created
                  {showSort && renderSortButton('createdOn')}
                </span>
              </th>
               <th className={thClass}>
                 First Name
                 {showSort && renderSortButton('firstName')}
               </th>
               <th className={thClass}>
                 Last Name
                 {showSort && renderSortButton('lastName')}
               </th>
                <th className={thClass}>
                 User Type
                 {showSort && renderSortButton('userType')}
               </th>
               <th className={`${thClass} min-w-[124px]`}>
                 Renew On
                 {showSort && renderSortButton('expiresOn')}
               </th>
               <th className={`${thClass} w-[84px] min-w-[84px]`}>
                 <span className="inline-flex items-center">
                   Credits
                   {showSort && renderSortButton('credits')}
                 </span>
               </th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => {
              const isEdited = !!editedUsers[u.uuid];
              const isSuperUser = u.userType === '1';
              const isClubAdmin = user?.userTypeId === '2';
              const canEdit = !(isClubAdmin && isSuperUser);
              const canEditUserType = canEdit && u.uuid !== user?.uuid;
              const isUnverified = u.verified === false;
  
              // Only super users can assign the Super User type; no one can edit their own type
              const dropdownUserTypes = isSuperUserLoggedIn
                ? userTypes
                : userTypes.filter(ut => ut.id !== '1');
              const isNewlyRegisteredUser = Number(u.userType) === 0 || String(u.userType).trim() === '0';
  
              return (
                <tr key={u.uuid} className={isEdited ? 'bg-yellow-100' : ''}>
                  <td className={tdClass}>{u.email}</td>
                  <td className={`${tdClass} w-[96px] min-w-[96px]`}>
                    <span className={isUnverified ? 'text-red-600 font-medium' : ''}>
                      {formatDateDDMMYYYY(u.createdOn)}
                    </span>
                  </td>
                  <td className={tdClass}>
                    {canEdit ? (
                      <input
                        type="text"
                        value={u.firstName}
                        onChange={e => handleEditUser(u.uuid, 'firstName', e.target.value)}
                        className={textInputInTableCellClass}
                      />
                    ) : (
                      u.firstName
                    )}
                  </td>
                  <td className={tdClass}>
                    {canEdit ? (
                      <input
                        type="text"
                        value={u.lastName}
                        onChange={e => handleEditUser(u.uuid, 'lastName', e.target.value)}
                        className={textInputInTableCellClass}
                      />
                    ) : (
                      u.lastName
                    )}
                  </td>
                    <td className={tdClass}>
                     {canEditUserType ? (
                       <select
                         value={u.userType}
                         onChange={e => handleEditUser(u.uuid, 'userType', e.target.value)}
                         className={`${selectClass} ${isNewlyRegisteredUser ? '!border-red-500' : ''}`}
                       >
                         {dropdownUserTypes.map(ut => (
                           <option key={ut.id} value={ut.id}>{ut.name}</option>
                         ))}
                       </select>
                     ) : (
                      <span className={isNewlyRegisteredUser ? 'text-red-600 font-medium' : ''}>
                         {userTypes.find(ut => ut.id === u.userType)?.name || u.userType}
                       </span>
                     )}
                   </td>
                  <td className={`${tdClass} min-w-[124px]`}>
                    {(() => {
                      // Find the user type to check if it has valid membership
                      const userType = userTypes.find(ut => ut.id === u.userType);
                      const hasValidMembership = userTypeHasValidMembership(userType);
                      
                      if (!hasValidMembership) {
                         return (
                           <div>
                             <DatePicker
                               selected={null}
                               onChange={() => {}} // No-op function
                               dateFormat="dd-MM-yyyy"
                               className={`${textInputInTableCellClass} text-slate-600 italic cursor-not-allowed`}
                               placeholderText="Not Applicable"
                               disabled={true}
                               readOnly={true}
                             />
                           </div>
                         );
                       }
                       
                        // Helper function to get expiry status
                        const getExpiryStatus = (expiryDate) => {
                          if (!expiryDate) return 'none';
                          const expiry = new Date(expiryDate);
                          const now = getSouthAfricaTime();
                          const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                          
                          if (daysUntilExpiry < 0) return 'expired';
                          if (daysUntilExpiry <= 7) return 'expiring';
                          return 'active';
                        };
                        
                        const expiryStatus = getExpiryStatus(u.expiresOn);
                        const borderClass = expiryStatus === 'expired' ? 'border-red-500' : 
                                          expiryStatus === 'expiring' ? 'border-amber-500' : 
                                          expiryStatus === 'none' ? 'border-red-500' : '';
                        
                        // Build the class string conditionally to avoid conflicts
                        const baseClass = "w-full text-sm px-3 py-2 bg-white border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 md:text-base h-[38px]";
                        const finalBorderClass = borderClass || 'border-slate-300';
                        const datePickerClass = `${baseClass} ${finalBorderClass}`;
                       
                       return canEdit ? (
                         <div>
                           <DatePicker
                             selected={u.expiresOn ? new Date(u.expiresOn) : null}
                             onChange={(date) => handleEditUser(u.uuid, 'expiresOn', date)}
                             dateFormat="dd-MM-yyyy"
                             customInput={<MaskedDateInput />}
                             className={datePickerClass}
                             placeholderText="dd-mm-yyyy"
                             renderCustomHeader={({ 
                               date,
                               changeYear,
                               changeMonth,
                               decreaseMonth,
                               increaseMonth,
                               prevMonthButtonDisabled,
                               nextMonthButtonDisabled,
                             }) => (
                               <div className="flex items-center justify-between px-2 py-2">
                                 <button
                                   onClick={decreaseMonth}
                                   disabled={prevMonthButtonDisabled}
                                   type="button"
                                   className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                 >
                                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                   </svg>
                                 </button>
                                 
                                 <div className="flex items-center gap-2">
                                   <select
                                     value={date.getMonth()}
                                     onChange={({ target: { value } }) => changeMonth(parseInt(value, 10))}
                                     className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none"
                                   >
                                     {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                                       <option key={month} value={index}>
                                         {month}
                                       </option>
                                     ))}
                                   </select>
                                   
                                   <select
                                     value={date.getFullYear()}
                                     onChange={({ target: { value } }) => changeYear(parseInt(value, 10))}
                                     className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none"
                                   >
                                     {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 50 + i).map((year) => (
                                       <option key={year} value={year}>
                                         {year}
                                       </option>
                                     ))}
                                   </select>
                                 </div>
                                 
                                 <button
                                   onClick={increaseMonth}
                                   disabled={nextMonthButtonDisabled}
                                   type="button"
                                   className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                 >
                                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                   </svg>
                                 </button>
                               </div>
                             )}
                           />
                         </div>
                       ) : (
                         <span>
                           {u.expiresOn ? new Date(u.expiresOn).toLocaleDateString() : ''}
                         </span>
                       );
                    })()}
                  </td>
                  <td className={`${tdClass} w-[84px] min-w-[84px]`}>
                    {canEdit ? (
                      <input
                        type="number"
                        step="0.01"
                        value={u.credits ?? 0}
                        onChange={e => handleEditUser(u.uuid, 'credits', e.target.value)}
                        className={`${textInputInTableCellClass} w-[84px] min-w-[84px]`}
                      />
                    ) : (
                      Number(u.credits ?? 0)
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );  

  const superUsers = sortUsersByField(users.filter(u => String(u.userType) === '1'));

  if (!user?.permissions?.clubAdmin?.canView) {
    return (
      <div>
        <ErrorModal
          isOpen={isErrorModalOpen}
          onClose={closeErrorModal}
          errorMessage={error}
        />
      </div>
    );
  }

  if (isDataLoading) {
    return (
      <div className="p-4 bg-slate-100 min-h-screen">
        <div className="shadow-md rounded-lg bg-white p-6 flex items-center justify-center text-slate-600">
          <div className="flex items-center gap-3">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading users...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-100 min-h-screen relative">
      <div className="relative flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">{`All Users (${users.length})`}</h2>
          {compactUsersSorted.length < users.length && (
            <p className="text-xs text-slate-500 mt-0.5">
              Showing {compactUsersSorted.length} of {users.length} users
            </p>
          )}
        </div>
        <div className="fixed top-30 right-4 z-20">
            <button
                onClick={handleUpdateAll}
                className={`py-2 px-4 rounded-md text-white font-medium transition-all duration-200 flex items-center justify-center disabled:opacity-50 ${
                loadingUpdate
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                }`}
                style={{ minWidth: 220 }}
                disabled={loadingUpdate || Object.keys(editedUsers).length === 0}
            >
                {loadingUpdate ? (
                <>
                    <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    ></circle>
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                    </svg>
                    Updating...
                </>
                ) : (() => {
                  const count = Object.keys(editedUsers).length;
                  return count > 0 ? `Update ${count} User${count === 1 ? '' : 's'}` : 'Update All';
                })()}
            </button>
            </div>
      </div>
      <section className="mb-8 p-2 md:p-3 bg-white rounded-lg shadow border-2 border-slate-300 overflow-hidden">
        <div className="overflow-x-auto -mx-2 md:-mx-3">
          <table className="min-w-full table-auto divide-y divide-slate-200">
            <thead>
              <tr className="bg-slate-100">
                <th className="sticky left-0 z-20 p-0 bg-slate-100" style={{ width: 4, minWidth: 4 }} aria-hidden="true" />
                <th className="px-2 py-1.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  <button type="button" ref={(el) => { filterButtonRefs.current.email = el; }}
                    className={`inline-flex items-center gap-1 hover:text-slate-800 font-semibold uppercase tracking-wider text-xs ${stringFilters.email.trim() ? 'text-blue-600' : ''}`}
                    onClick={() => openFilterPopup('email')}
                  >Email</button>
                  {renderCompactSortButton('email')}
                </th>
                <th className="px-2 py-1.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  <button type="button" ref={(el) => { filterButtonRefs.current.firstName = el; }}
                    className={`inline-flex items-center gap-1 hover:text-slate-800 font-semibold uppercase tracking-wider text-xs ${stringFilters.firstName.trim() ? 'text-blue-600' : ''}`}
                    onClick={() => openFilterPopup('firstName')}
                  >First Name</button>
                  {renderCompactSortButton('firstName')}
                </th>
                <th className="px-2 py-1.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  <button type="button" ref={(el) => { filterButtonRefs.current.lastName = el; }}
                    className={`inline-flex items-center gap-1 hover:text-slate-800 font-semibold uppercase tracking-wider text-xs ${stringFilters.lastName.trim() ? 'text-blue-600' : ''}`}
                    onClick={() => openFilterPopup('lastName')}
                  >Last Name</button>
                  {renderCompactSortButton('lastName')}
                </th>
                <th className="px-2 py-1.5 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  <button type="button" ref={(el) => { filterButtonRefs.current.createdOn = el; }}
                    className={`inline-flex items-center gap-1 hover:text-slate-800 font-semibold uppercase tracking-wider text-xs ${isDateFilterActive('createdOn') ? 'text-blue-600' : ''}`}
                    onClick={() => openFilterPopup('createdOn')}
                  >Created On</button>
                  {renderCompactSortButton('createdOn')}
                </th>
                <th className="px-2 py-1.5 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  <button type="button" ref={(el) => { filterButtonRefs.current.lastLogin = el; }}
                    className={`inline-flex items-center gap-1 hover:text-slate-800 font-semibold uppercase tracking-wider text-xs ${isDateFilterActive('lastLogin') ? 'text-blue-600' : ''}`}
                    onClick={() => openFilterPopup('lastLogin')}
                  >Last Login</button>
                  {renderCompactSortButton('lastLogin')}
                </th>
                <th className="px-2 py-1.5 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  <button
                    type="button"
                    ref={compactTypeFilterButtonRef}
                    className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-xs hover:text-slate-800 ${isUserTypeFilterActive ? 'text-blue-600' : 'text-slate-700'}`}
                    onClick={handleCompactTypeFilterToggle}
                  >
                    <span>USER TYPES</span>
                    <svg className={`h-3.5 w-3.5 transition-transform ${isUserTypeFilterActive ? 'text-blue-600' : 'text-slate-600'} ${showCompactTypeFilter ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                  {renderCompactSortButton('userType')}
                </th>
                {showStatusColumn && (
                  <th className="px-2 py-1.5 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                    <button
                      type="button"
                      ref={compactStatusFilterButtonRef}
                      className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-xs hover:text-slate-800 ${isStatusFilterActive ? 'text-blue-600' : 'text-slate-700'}`}
                      onClick={handleCompactStatusFilterToggle}
                    >
                      <span>Status</span>
                      <svg className={`h-3.5 w-3.5 transition-transform ${isStatusFilterActive ? 'text-blue-600' : 'text-slate-600'} ${showCompactStatusFilter ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                    {renderCompactSortButton('memberStatus')}
                  </th>
                )}
                <th className="px-2 py-1.5 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  <button type="button" ref={(el) => { filterButtonRefs.current.expiresOn = el; }}
                    className={`inline-flex items-center gap-1 hover:text-slate-800 font-semibold uppercase tracking-wider text-xs ${isDateFilterActive('expiresOn') ? 'text-blue-600' : ''}`}
                    onClick={() => openFilterPopup('expiresOn')}
                  >Expires On</button>
                  {renderCompactSortButton('expiresOn')}
                </th>
                <th className="px-2 py-1.5 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                  Credits
                  {renderCompactSortButton('credits')}
                </th>
              </tr>
            </thead>
            <tbody>
              {compactUsersSorted.map((u, idx) => {
                const isEdited = !!editedUsers[u.uuid];
                const isEvenRow = idx % 2 === 1;
                const isSuperUser = String(u.userType) === '1';
                const isClubAdmin = user?.userTypeId === '2';
                const canEditCompact = !(isClubAdmin && isSuperUser);
                const canEditUserType = canEditCompact && u.uuid !== user?.uuid;
                const expiryStatus = getExpiryStatus(u.expiresOn);
                const isExpired = expiryStatus === 'expired';
                const expiryTextClass = expiryStatus === 'expired'
                  ? 'text-red-600 font-medium'
                  : expiryStatus === 'expiring'
                    ? 'text-amber-600 font-medium'
                    : '';
                const hasValidMembership = hasMembershipForUserType(u.userType);

                const editedFields = isEdited && typeof editedUsers[u.uuid] === 'object' ? editedUsers[u.uuid] : {};
                const EditDot = () => <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" aria-hidden="true" />;
                const StatusBadge = ({ children, className }) => (
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${className}`}>{children}</span>
                );
                const userTypeBadgeClass = String(u.userType) === '1'
                  ? 'bg-purple-100 text-purple-700'
                  : String(u.userType) === '0'
                    ? STATUS_BADGE_CLASSES.red
                    : hasValidMembership
                      ? 'bg-green-100 text-green-700'
                      : 'bg-blue-100 text-blue-700';
                const expiryBadgeClass = expiryStatus === 'expired'
                  ? STATUS_BADGE_CLASSES.red
                  : expiryStatus === 'expiring'
                    ? STATUS_BADGE_CLASSES.orange
                    : null;

                return (
                  <tr key={`compact-${u.uuid}`} className={`group border-b border-slate-100 hover:bg-slate-100 ${isEvenRow ? 'bg-slate-50' : 'bg-white'}`}>
                    <td className={`sticky left-0 z-20 p-0 transition-colors ${isEdited ? 'bg-amber-400' : isExpired ? 'bg-red-400' : isEvenRow ? 'bg-slate-50 group-hover:bg-slate-100' : 'bg-white group-hover:bg-slate-100'}`} style={{ width: 4, minWidth: 4 }} aria-hidden="true" />
                    <td className="px-2 py-1.5 text-xs md:text-sm max-w-[160px] md:max-w-[220px]">
                      {canEditCompact ? (
                        <button
                          type="button"
                          onClick={() => openUserActionPopup(u)}
                          className="inline-block max-w-full truncate bg-transparent border-0 p-0 text-left text-slate-700 hover:text-blue-600"
                        >
                          {u.email}
                        </button>
                      ) : (
                        <Tooltip content={u.email} block>
                          <span className="block truncate text-slate-700">{u.email}</span>
                        </Tooltip>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-slate-700">
                      {canEditCompact ? (
                        <button
                          type="button"
                          onClick={() => openCompactTextEditor(u, 'firstName')}
                          className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 hover:text-blue-600 text-left"
                        >
                          {u.firstName || ''}
                          {editedFields.firstName && <EditDot />}
                        </button>
                      ) : (
                        u.firstName || ''
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-slate-700">
                      {canEditCompact ? (
                        <button
                          type="button"
                          onClick={() => openCompactTextEditor(u, 'lastName')}
                          className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 hover:text-blue-600 text-left"
                        >
                          {u.lastName || ''}
                          {editedFields.lastName && <EditDot />}
                        </button>
                      ) : (
                        u.lastName || ''
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-center tabular-nums text-slate-700">
                      <span className="inline-block -translate-x-2">{formatDateDDMMYYYY(u.createdOn)}</span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-slate-700 text-center tabular-nums">
                      <span className="inline-flex items-center -translate-x-2">
                        {u.lastLogin
                          ? formatDateDDMMYYYY(u.lastLogin)
                          : u.verified === false
                            ? <StatusBadge className="bg-yellow-100 text-yellow-700">Unverified</StatusBadge>
                            : '-'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-center">
                      {canEditUserType ? (
                        <button
                          type="button"
                          onClick={() => openCompactUserTypeEditor(u)}
                          className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 hover:opacity-75"
                        >
                          <StatusBadge className={userTypeBadgeClass}>{getUserTypeName(u.userType)}</StatusBadge>
                          {editedFields.userType && <EditDot />}
                        </button>
                      ) : (
                        <StatusBadge className={userTypeBadgeClass}>{getUserTypeName(u.userType)}</StatusBadge>
                      )}
                    </td>
                    {showStatusColumn && (
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-center">
                        {(() => {
                          const statusDisplay = getAdminRowStatusDisplay(
                            u.memberStatus,
                            userTypeById.get(String(u.userType)),
                            { isExpired }
                          );

                          if (statusDisplay.kind === 'empty') {
                            return <span className="text-slate-400">-</span>;
                          }

                          const badge = (
                            <span className={`${STATUS_BADGE_BASE_CLASS} ${statusDisplay.badgeClass}`}>
                              {statusDisplay.label}
                            </span>
                          );

                          if (!canViewAdminMetaData || !statusDisplay.clickable) {
                            return badge;
                          }

                          return (
                            <button
                              type="button"
                              onClick={() => openApplicationEditor(u)}
                              className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 hover:opacity-75"
                            >
                              {badge}
                              {editedFields.memberStatus && <EditDot />}
                            </button>
                          );
                        })()}
                      </td>
                    )}
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-center tabular-nums text-slate-700">
                      {!hasValidMembership ? (
                        <span className="inline-block -translate-x-2">-</span>
                      ) : canEditCompact ? (
                        <button
                          type="button"
                          onClick={() => openCompactExpiryEditor(u)}
                          className="inline-flex items-center gap-1.5 -translate-x-2 bg-transparent border-0 p-0 hover:opacity-75"
                        >
                          {u.expiresOn
                            ? expiryBadgeClass
                              ? <StatusBadge className={expiryBadgeClass}>{formatDateDDMMYYYY(u.expiresOn)}</StatusBadge>
                              : formatDateDDMMYYYY(u.expiresOn)
                            : <StatusBadge className={STATUS_BADGE_CLASSES.red}>Missing</StatusBadge>}
                          {editedFields.expiresOn && <EditDot />}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 -translate-x-2">
                          {u.expiresOn
                            ? expiryBadgeClass
                              ? <StatusBadge className={expiryBadgeClass}>{formatDateDDMMYYYY(u.expiresOn)}</StatusBadge>
                              : formatDateDDMMYYYY(u.expiresOn)
                            : <StatusBadge className={STATUS_BADGE_CLASSES.red}>Missing</StatusBadge>}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs md:text-sm text-right tabular-nums">
                      {canEditCompact ? (
                        <button
                          type="button"
                          onClick={() => openCompactCreditsEditor(u)}
                          className={`inline-flex items-center gap-1.5 -translate-x-2 bg-transparent border-0 p-0 hover:text-blue-600 ${Number(u.credits ?? 0) === 0 ? 'text-slate-400' : 'text-slate-700'}`}
                        >
                          {Number(u.credits ?? 0)}
                          {editedFields.credits && <EditDot />}
                        </button>
                      ) : (
                        <span className={`inline-block -translate-x-2 ${Number(u.credits ?? 0) === 0 ? 'text-slate-400' : 'text-slate-700'}`}>{Number(u.credits ?? 0)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {compactUsersSorted.length === 0 && (
                <tr>
                  <td colSpan={showStatusColumn ? 10 : 9} className="px-2 py-3 text-sm text-slate-500 text-center">
                    No users match the active filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {activeFilterPopup && (() => {
          const isStringField = ['email', 'firstName', 'lastName'].includes(activeFilterPopup);
          const isDateField = ['createdOn', 'lastLogin', 'expiresOn'].includes(activeFilterPopup);
          if (!isStringField && !isDateField) return null;
          const field = activeFilterPopup;

          const renderStringContent = () => {
            const labelMap = { email: 'Email', firstName: 'First Name', lastName: 'Last Name' };
            const value = stringFilters[field];
            return (
              <>
                <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">
                  Filter by {labelMap[field]}
                </label>
                <input
                  type="text"
                  autoFocus
                  value={value}
                  onChange={(e) => setStringFilters((prev) => ({ ...prev, [field]: e.target.value }))}
                  placeholder="Type to filter…"
                  className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                {value && (
                  <div className="mt-2 flex items-center justify-end">
                    <button type="button"
                      onClick={() => setStringFilters((prev) => ({ ...prev, [field]: '' }))}
                      className="px-2 py-1 text-xs text-slate-600 hover:text-slate-800"
                    >Clear</button>
                  </div>
                )}
              </>
            );
          };

          const renderDateContent = () => {
            const tree = buildDateTree(field);
            const allKeys = getAllDateKeysForField(field);
            const selectedSet = dateFilterSets[field];
            const selCount = dateFilterSelected[field].length;
            const allSelected = allKeys.length > 0 && selCount === allKeys.length;
            const someSelected = selCount > 0 && selCount < allKeys.length;
            return (
              <>
                <label className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600 cursor-pointer select-none">
                  <input type="checkbox"
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={(e) => setDateFilterSelected((prev) => ({ ...prev, [field]: e.target.checked ? [...allKeys] : [] }))}
                  />
                  <span>(Select All)</span>
                </label>
                <div className="border-t my-1" />
                {Object.keys(tree).map((year) => {
                  const yearKeys = Object.keys(tree[year]).flatMap((m) => tree[year][m].map((d) => `${year}-${m}-${d}`));
                  const allYear = yearKeys.every((k) => selectedSet.has(k));
                  const someYear = yearKeys.some((k) => selectedSet.has(k));
                  const yearExpKey = `${field}-${year}`;
                  const yearExpanded = !!dateTreeExpanded[yearExpKey];
                  return (
                    <div key={year}>
                      <div className="flex items-center gap-1 px-2 py-1">
                        <button type="button"
                          onClick={() => setDateTreeExpanded((prev) => ({ ...prev, [yearExpKey]: !prev[yearExpKey] }))}
                          className="text-slate-400 hover:text-slate-600 w-3.5 flex-shrink-0 text-xs leading-none"
                        >{yearExpanded ? '▼' : '▶'}</button>
                        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                          <input type="checkbox"
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            checked={allYear}
                            ref={(el) => { if (el) el.indeterminate = someYear && !allYear; }}
                            onChange={() => setDateFilterSelected((prev) => {
                              const cur = new Set(prev[field]);
                              if (allYear) yearKeys.forEach((k) => cur.delete(k));
                              else yearKeys.forEach((k) => cur.add(k));
                              return { ...prev, [field]: [...cur] };
                            })}
                          />
                          <span>{year}</span>
                        </label>
                      </div>
                      {yearExpanded && Object.keys(tree[year]).map((month) => {
                        const monthKeys = tree[year][month].map((d) => `${year}-${month}-${d}`);
                        const allMonth = monthKeys.every((k) => selectedSet.has(k));
                        const someMonth = monthKeys.some((k) => selectedSet.has(k));
                        const monthExpKey = `${field}-${year}-${month}`;
                        const monthExpanded = !!dateTreeExpanded[monthExpKey];
                        const monthName = new Date(Number(year), Number(month) - 1, 1).toLocaleString('en-GB', { month: 'long' });
                        return (
                          <div key={monthExpKey}>
                            <div className="flex items-center gap-1 pl-6 pr-2 py-1">
                              <button type="button"
                                onClick={() => setDateTreeExpanded((prev) => ({ ...prev, [monthExpKey]: !prev[monthExpKey] }))}
                                className="text-slate-400 hover:text-slate-600 w-3.5 flex-shrink-0 text-xs leading-none"
                              >{monthExpanded ? '▼' : '▶'}</button>
                              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                                <input type="checkbox"
                                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                                  checked={allMonth}
                                  ref={(el) => { if (el) el.indeterminate = someMonth && !allMonth; }}
                                  onChange={() => setDateFilterSelected((prev) => {
                                    const cur = new Set(prev[field]);
                                    if (allMonth) monthKeys.forEach((k) => cur.delete(k));
                                    else monthKeys.forEach((k) => cur.add(k));
                                    return { ...prev, [field]: [...cur] };
                                  })}
                                />
                                <span>{monthName}</span>
                              </label>
                            </div>
                            {monthExpanded && tree[year][month].map((day) => {
                              const dateKey = `${year}-${month}-${day}`;
                              const isDaySelected = selectedSet.has(dateKey);
                              return (
                                <label key={day} className="flex items-center gap-2 pl-14 pr-2 py-1 text-sm text-slate-600 cursor-pointer select-none">
                                  <input type="checkbox"
                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                                    checked={isDaySelected}
                                    onChange={() => setDateFilterSelected((prev) => {
                                      const cur = new Set(prev[field]);
                                      if (isDaySelected) cur.delete(dateKey); else cur.add(dateKey);
                                      return { ...prev, [field]: [...cur] };
                                    })}
                                  />
                                  <span>{Number(day)}</span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </>
            );
          };

          return (
            <div
              ref={filterPopupRef}
              className={`fixed z-50 bg-white border border-slate-200 rounded-md shadow-lg normal-case tracking-normal ${
                isStringField ? 'p-3 w-[280px]' : 'p-2 w-64 max-h-80 overflow-y-auto'
              }`}
              style={{ top: filterPopupPosition.top, left: filterPopupPosition.left }}
            >
              {isStringField ? renderStringContent() : renderDateContent()}
            </div>
          );
        })()}
        {showCompactTypeFilter && (
          <div
            ref={compactTypeFilterDropdownRef}
            className="fixed w-64 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg p-2 z-50 normal-case tracking-normal"
            style={{ top: compactTypeFilterPosition.top, left: compactTypeFilterPosition.left }}
          >
            <label className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                checked={compactUserTypeOptions.length > 0 && selectedUserTypeFilters.length === compactUserTypeOptions.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedUserTypeFilters(compactUserTypeOptions.map((ut) => String(ut.id)));
                  } else {
                    setSelectedUserTypeFilters([]);
                  }
                }}
              />
              <span>Select All</span>
            </label>
            <div className="border-t my-1" />
            {compactUserTypeOptions.map((ut) => {
              const checked = selectedUserTypeFilters.includes(String(ut.id));
              return (
                <label key={ut.id} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    checked={checked}
                    onChange={() => toggleUserTypeFilter(ut.id)}
                  />
                  <span>{ut.name || `User Type ${ut.id}`}</span>
                </label>
              );
            })}
            {compactUserTypeOptions.length === 0 && (
              <span className="px-2 py-1 text-sm text-slate-500">No user types available.</span>
            )}
          </div>
        )}
        {showCompactStatusFilter && (
          <div
            ref={compactStatusFilterDropdownRef}
            className="fixed w-64 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg p-2 z-50 normal-case tracking-normal"
            style={{ top: compactStatusFilterPosition.top, left: compactStatusFilterPosition.left }}
          >
            <label className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                checked={ADMIN_STATUS_FILTER_OPTIONS.length > 0 && selectedStatusFilters.length === ADMIN_STATUS_FILTER_OPTIONS.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedStatusFilters(ADMIN_STATUS_FILTER_OPTIONS.map((option) => option.key));
                  } else {
                    setSelectedStatusFilters([]);
                  }
                }}
              />
              <span>Select All</span>
            </label>
            <div className="border-t my-1" />
            {ADMIN_STATUS_FILTER_OPTIONS.map((option) => {
              const checked = selectedStatusFilters.includes(option.key);
              return (
                <label key={option.key} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    checked={checked}
                    onChange={() => toggleStatusFilter(option.key)}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Edit modals – centred overlay, keyboard-safe on mobile ── */}

      {compactTextEditor && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setCompactTextEditor(null)}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-screen items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-base font-semibold text-slate-800 mb-4">
                {compactTextEditor.field === 'firstName' ? 'Edit First Name' : 'Edit Last Name'}
              </h3>
              <input
                type="text"
                value={compactTextEditor.value}
                onChange={(e) => setCompactTextEditor((prev) => ({ ...prev, value: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveCompactTextEditor();
                  if (e.key === 'Escape') setCompactTextEditor(null);
                }}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                autoFocus
              />
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors" onClick={() => setCompactTextEditor(null)}>Cancel</button>
                <button type="button" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" onClick={saveCompactTextEditor}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {compactUserTypeEditor && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setCompactUserTypeEditor(null)}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-screen items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Edit User Type for {compactUserTypeEditor.firstName} {compactUserTypeEditor.lastName}</h3>
              <select
                value={compactUserTypeEditor.value}
                onChange={(e) => setCompactUserTypeEditor((prev) => ({ ...prev, value: e.target.value }))}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                autoFocus
              >
                {userTypes
                  .filter((ut) => isSuperUserLoggedIn || String(ut.id) !== '1')
                  .map((ut) => (
                    <option key={ut.id} value={String(ut.id)}>{ut.name}</option>
                  ))}
              </select>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors" onClick={() => setCompactUserTypeEditor(null)}>Cancel</button>
                <button type="button" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" onClick={saveCompactUserTypeEditor}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {compactCreditsEditor && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setCompactCreditsEditor(null)}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-screen items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-base font-semibold text-slate-800 mb-1">Add credits for {compactCreditsEditor.firstName} {compactCreditsEditor.lastName}</h3>
              <p className="text-sm text-slate-500 mb-4">Current balance: <span className="font-medium text-slate-700">{compactCreditsEditor.currentCredits}</span></p>
              <input
                type="number"
                min="0"
                step="0.01"
                value={compactCreditsEditor.addAmount}
                onChange={(e) => setCompactCreditsEditor((prev) => ({ ...prev, addAmount: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveCompactCreditsEditor();
                  if (e.key === 'Escape') setCompactCreditsEditor(null);
                }}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Amount to add"
                autoFocus
              />
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors" onClick={() => setCompactCreditsEditor(null)}>Cancel</button>
                <button type="button" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" onClick={saveCompactCreditsEditor}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {compactExpiryEditor && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setCompactExpiryEditor(null)}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-screen items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-[320px]">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Edit Expiry Date for {compactExpiryEditor.firstName} {compactExpiryEditor.lastName}</h3>
              <InputMask
                mask="99-99-9999"
                maskChar={null}
                inputMode="numeric"
                placeholder="dd-mm-yyyy"
                value={compactExpiryEditor.value || ''}
                onChange={(e) => setCompactExpiryEditor((prev) => ({ ...prev, value: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveCompactExpiryEditor();
                  if (e.key === 'Escape') setCompactExpiryEditor(null);
                }}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 mb-4"
              />
              <DatePicker
                  selected={parseInputDate(compactExpiryEditor.value)}
                  onChange={handleCompactExpiryChange}
                  inline
                  dateFormat="dd-MM-yyyy"
                  renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                    <div className="flex items-center justify-between px-2 py-2">
                      <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} type="button" className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      </button>
                      <div className="flex items-center gap-2">
                        <select value={date.getMonth()} onChange={({ target: { value } }) => changeMonth(parseInt(value, 10))} className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none">
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((month, index) => (
                            <option key={month} value={index}>{month}</option>
                          ))}
                        </select>
                        <select value={date.getFullYear()} onChange={({ target: { value } }) => changeYear(parseInt(value, 10))} className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none">
                          {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 50 + i).map((year) => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} type="button" className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                  )}
                />
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors" onClick={() => setCompactExpiryEditor(null)}>Cancel</button>
                <button type="button" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" onClick={saveCompactExpiryEditor}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {userActionPopup && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setUserActionPopup(null)}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-screen items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="mb-4 pb-4 border-b border-slate-100">
                <p className="text-base font-semibold text-slate-800">{userActionPopup.user.firstName} {userActionPopup.user.lastName}</p>
                <p className="text-sm text-slate-500 truncate mt-0.5">{userActionPopup.user.email}</p>
              </div>
              {userActionPopup.user.uuid !== user?.uuid ? (
                <button
                  type="button"
                  onClick={() => {
                    setDeleteConfirmUser(userActionPopup.user);
                    setUserActionPopup(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                  Delete User
                </button>
              ) : (
                <p className="text-sm text-slate-400 py-1">No actions available for your own account.</p>
              )}
              <div className="mt-6 flex justify-end">
                <button type="button" className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors" onClick={() => setUserActionPopup(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {metaDataEditor?.loading && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setMetaDataEditor(null)}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-screen items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <p className="text-sm text-slate-600">Loading application data...</p>
            </div>
          </div>
        </div>
      )}

      {metaDataEditor && !metaDataEditor.loading && metaDataApplicationHtml && (
        <ClubTermsModal
          isOpen
          onClose={() => setMetaDataEditor(null)}
          htmlContent={metaDataApplicationHtml}
          initialFormData={metaDataEditor.fields}
          showAccept={canEditAdminMetaData}
          acceptLabel="Save"
          softValidate={canEditAdminMetaData}
          onAccept={handleStageApplicationSave}
          readOnly={!canEditAdminMetaData}
          errorMessage={metaDataEditor.error}
          prependHtml={applicationRejectionPrependHtml}
          leftFooterActions={getApplicationWorkflowLeftActions()}
          membershipTypeName={metaDataApplicationUserType?.name}
          membershipColour={metaDataApplicationUserType?.membership?.colour}
        />
      )}

      {rejectApplicationPrompt && (
        <div className="fixed inset-0 z-[60] overflow-y-auto" onClick={() => setRejectApplicationPrompt(null)}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-screen items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-800">Reject Application</h3>
              <p className="mt-2 text-sm text-slate-600">
                Please provide a reason for rejecting{' '}
                {rejectApplicationPrompt.user.firstName} {rejectApplicationPrompt.user.lastName}&apos;s application.
                The applicant will see this message when they view their application.
              </p>
              <label className="block mt-4">
                <span className="block text-sm font-medium text-slate-700 mb-1">Rejection reason</span>
                <textarea
                  value={rejectApplicationReason}
                  onChange={(e) => setRejectApplicationReason(e.target.value)}
                  rows={4}
                  className="w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Explain why this application was not approved"
                />
              </label>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setRejectApplicationPrompt(null);
                    setRejectApplicationReason('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRejectApplicationConfirm}
                  disabled={!rejectApplicationReason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Reject Application
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!deleteConfirmUser}
        onClose={() => { if (!loadingDelete) setDeleteConfirmUser(null); }}
        onConfirm={async () => {
          if (!deleteConfirmUser) return;
          setLoadingDelete(true);
          try {
            await deleteUser(deleteConfirmUser.uuid);
            setUsers((prev) => prev.filter((u) => u.uuid !== deleteConfirmUser.uuid));
            setDeleteConfirmUser(null);
          } catch (err) {
            handleError(err?.message || 'Failed to delete user. Please try again.');
            setDeleteConfirmUser(null);
          } finally {
            setLoadingDelete(false);
          }
        }}
        title="Delete User"
        message={`Are you sure you want to permanently delete ${deleteConfirmUser?.firstName} ${deleteConfirmUser?.lastName} (${deleteConfirmUser?.email})? This action cannot be undone.`}
        confirmText="Delete User"
        isDestructive
        isLoading={loadingDelete}
      />

      <ErrorModal isOpen={isErrorModalOpen} onClose={closeErrorModal} errorMessage={error} />
    </div>
  );
};

export default ClubAdmin;
